import { X } from "lucide-react";
import Image from "next/image";
import { useEffect } from "react";
import { Button } from "../ui/button";

interface SwapSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  inputAmount: string;
  outputAmount: string;
  inputTokenSymbol: string;
  outputTokenSymbol: string;
}

export default function SwapSuccessModal({
  isOpen,
  onClose,
  inputAmount,
  outputAmount,
  inputTokenSymbol,
  outputTokenSymbol,
}: SwapSuccessModalProps) {
  useEffect(() => {
    if (isOpen) {
      // Prevent scrolling when modal is open
      document.body.style.overflow = "hidden";
    } else {
      // Re-enable scrolling when modal is closed
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
      <div className="relative max-w-md w-full mx-4 bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl shadow-2xl overflow-hidden border-2 border-green-400 animate-fadeIn">
        <div className="absolute top-2 right-2">
          <Button onClick={onClose} className="p-1 rounded-full bg-white/10 hover:bg-white/20 transition-colors">
            <X size={20} className="text-white" />
          </Button>
        </div>

        <div className="p-6 text-center">
          <div className="flex justify-center mb-4">
            <div className="relative w-20 h-20">
              <div className="absolute inset-0 bg-green-400 rounded-full opacity-20 animate-ping" />
              <div className="relative flex items-center justify-center w-20 h-20 bg-green-500 rounded-full">
                <svg
                  className="w-10 h-10 text-white"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <title>decoration</title>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-white mb-2">🎉 Swap Successful! 🎉</h2>

          <div className="bg-white/10 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center">
                <Image
                  src={`/token_logos/${inputTokenSymbol.toLowerCase()}.png`}
                  alt={inputTokenSymbol}
                  width={24}
                  height={24}
                  className="rounded-full mr-2"
                />
                <span className="text-white">
                  {inputAmount} {inputTokenSymbol}
                </span>
              </div>
              <span className="text-white">→</span>
              <div className="flex items-center">
                <Image
                  src={`/token_logos/${outputTokenSymbol.toLowerCase()}.png`}
                  alt={outputTokenSymbol}
                  width={24}
                  height={24}
                  className="rounded-full mr-2"
                />
                <span className="text-white">
                  {outputAmount} {outputTokenSymbol}
                </span>
              </div>
            </div>
          </div>

          <Button
            onClick={onClose}
            className="w-full py-3 bg-white/20 hover:bg-white/30 text-white font-medium rounded-lg transition-colors"
          >
            Continue Trading
          </Button>
        </div>
      </div>
    </div>
  );
}
